import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CompanyCustomField } from 'services/actions/ai_management_proposal_company';
import {
  ProposalConditionsResponse,
  getProposalConditions,
  updateProposalConditions,
} from 'services/actions/ai_management_proposal_conditions';

interface Options {
  trackSave: <T>(request: Promise<T>) => Promise<T>;
}

// Rascunho em texto (como nos inputs); vai inteiro em cada gravação, porque o PUT substitui tudo.
export interface ConditionsDraft {
  validade_dias: string;
  prazo_entrega_dias: string;
  condicoes_pagamento: string;
  garantia: string;
  campos_personalizados: CompanyCustomField[];
}

export type ConditionErrorField = 'validade_dias' | 'prazo_entrega_dias' | 'campos_personalizados';

export interface ConditionLimits {
  validadeMinima: number | null;
  prazoMaximo: number | null;
}

const SAVE_DELAY_MS = 800;

const toText = (value: number | string | null | undefined) =>
  value === null || value === undefined ? '' : String(value);

const toDraft = (body: ProposalConditionsResponse): ConditionsDraft => ({
  validade_dias: toText(body.valores?.validade_dias),
  prazo_entrega_dias: toText(body.valores?.prazo_entrega_dias),
  condicoes_pagamento: toText(body.valores?.condicoes_pagamento),
  garantia: toText(body.valores?.garantia),
  campos_personalizados: (body.campos_personalizados ?? []).map((c) => ({ ...c })),
});

// Só dígitos nos dias: "1.5", "1e3" ou "-5" levariam a 422 e, como o PUT é tudo ou nada,
// travariam todas as condições.
export const apenasDigitos = (value: string) => value.replace(/\D/g, '');

// O back lê os dias com Integer() do Ruby, que trata zero à esquerda como octal ("060" vira 48).
const semZerosAEsquerda = (value: string) => value.replace(/^0+(?=\d)/, '');

// Validade abaixo do mínimo e prazo acima do máximo do edital; vazio não é "fora da regra".
export const validadeAbaixo = (value: string, minimo: number | null) =>
  minimo !== null && value.trim() !== '' && Number(value) < minimo;

export const prazoAcima = (value: string, maximo: number | null) =>
  maximo !== null && value.trim() !== '' && Number(value) > maximo;

// A mensagem do back (422) diz qual condição falhou; o resto é dos campos personalizados.
const errorField = (message: string): ConditionErrorField => {
  if (message.startsWith('Validade')) return 'validade_dias';
  if (message.startsWith('Prazo')) return 'prazo_entrega_dias';
  return 'campos_personalizados';
};

// O interceptor global do Api resolve 4xx sem rejeitar: o erro vem pelo status/corpo.
const errorOf = (res: { data: unknown; status?: number }): string | null => {
  const status = res?.status ?? 200;
  const body = res?.data as { error?: string } | undefined;
  if (status >= 400 || body?.error) return body?.error || 'Não foi possível salvar as condições.';
  return null;
};

// Fila única, fora do componente: ao voltar à etapa, a nova instância espera a gravação pendente
// da anterior (senão leria o valor antigo e a próxima edição o regravaria).
let gravacoes: Promise<unknown> = Promise.resolve();

export function useProposalConditions(biddingId: number | null, { trackSave }: Options) {
  const [draft, setDraft] = useState<ConditionsDraft | null>(null);
  const [limits, setLimits] = useState<ConditionLimits>({
    validadeMinima: null,
    prazoMaximo: null,
  });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saveError, setSaveError] = useState<{
    field: ConditionErrorField;
    message: string;
  } | null>(null);

  const biddingRef = useRef(biddingId);
  biddingRef.current = biddingId;
  const mounted = useRef(true);
  // Último rascunho por licitação: a gravação lê dele quando sai da fila, nunca do estado da tela,
  // para uma gravação atrasada não mandar o rascunho de outra licitação.
  const latest = useRef(new Map<number, ConditionsDraft>());
  const queued = useRef(new Set<number>());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  // Uma gravação por vez; edições durante o voo entram numa única gravação seguinte (a última vence).
  const enqueue = useCallback(
    (id: number) => {
      if (queued.current.has(id)) return;
      queued.current.add(id);
      const pedido = gravacoes.then(() => {
        queued.current.delete(id);
        const atual = latest.current.get(id);
        if (!atual) return undefined;
        const { campos_personalizados: campos, ...valores } = atual;
        return updateProposalConditions(id, {
          ...valores,
          validade_dias: semZerosAEsquerda(valores.validade_dias),
          prazo_entrega_dias: semZerosAEsquerda(valores.prazo_entrega_dias),
          campos_personalizados: campos,
        });
      });
      gravacoes = pedido.catch(() => undefined);
      // O indicador da casca acompanha a gravação desde que entra na fila, na licitação que a pediu.
      trackSave(pedido)
        .then((res) => {
          if (!res || !mounted.current || biddingRef.current !== id) return;
          const message = errorOf(res);
          setSaveError(message ? { field: errorField(message), message } : null);
        })
        .catch(() => {
          if (!mounted.current || biddingRef.current !== id) return;
          setSaveError({
            field: 'campos_personalizados',
            message: 'Não foi possível salvar as condições.',
          });
        });
    },
    [trackSave],
  );

  // Ao trocar de licitação ou sair da etapa, grava na hora o que estava esperando o debounce.
  useEffect(
    () => () => {
      if (timer.current && biddingId) {
        clearTimeout(timer.current);
        timer.current = null;
        enqueue(biddingId);
      }
    },
    [biddingId, enqueue],
  );

  const load = useCallback(() => {
    if (!biddingId) return;
    const id = biddingId;
    setDraft(null);
    setLoading(true);
    setLoadError(false);
    setSaveError(null);
    gravacoes
      .then(() => getProposalConditions(id))
      .then((res) => {
        if (!mounted.current || biddingRef.current !== id) return;
        const body = res?.data as ProposalConditionsResponse | undefined;
        if (errorOf(res) || !body?.valores) {
          setLoadError(true);
          return;
        }
        const carregado = toDraft(body);
        latest.current.set(id, carregado);
        setDraft(carregado);
        setLimits({
          validadeMinima: body.minimos?.validade_dias ?? null,
          prazoMaximo: body.minimos?.prazo_entrega_dias ?? null,
        });
      })
      .catch(() => {
        if (mounted.current && biddingRef.current === id) setLoadError(true);
      })
      .finally(() => {
        if (mounted.current && biddingRef.current === id) setLoading(false);
      });
  }, [biddingId]);

  useEffect(() => {
    load();
  }, [load]);

  // Só grava depois que as condições da licitação carregaram: gravar antes zeraria o que está salvo.
  const update = useCallback(
    (patch: Partial<ConditionsDraft>) => {
      if (!biddingId) return;
      const id = biddingId;
      const base = latest.current.get(id);
      if (!base) return;
      const next = { ...base, ...patch };
      latest.current.set(id, next);
      setDraft(next);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        timer.current = null;
        enqueue(id);
      }, SAVE_DELAY_MS);
    },
    [biddingId, enqueue],
  );

  // Pendência: com regra, validade vazia ou abaixo do mínimo e prazo vazio ou acima do máximo.
  const pendencies = useMemo(() => {
    if (!draft) return 0;
    const { validadeMinima, prazoMaximo } = limits;
    const validade =
      validadeMinima !== null &&
      (!draft.validade_dias.trim() || validadeAbaixo(draft.validade_dias, validadeMinima));
    const prazo =
      prazoMaximo !== null &&
      (!draft.prazo_entrega_dias.trim() || prazoAcima(draft.prazo_entrega_dias, prazoMaximo));
    return (validade ? 1 : 0) + (prazo ? 1 : 0);
  }, [draft, limits]);

  return { draft, limits, loading, loadError, saveError, pendencies, update, reload: load };
}
